// Google Ads / GA4 Tracking
// -> Trage hier deine echten IDs ein:
export const GOOGLE_ADS_ID = "AW-983495284"; // Google Ads Conversion-ID
export const GOOGLE_ADS_CONVERSION_LABEL = "3NCQCPTA8fkcEPTk-9QD"; // Label: SHOPIFY-FREELANCER FORMULAR
// GA4 Mess-ID kommt aus der verknuepften Google-Analytics-Verbindung.
export const GA4_ID: string =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined) ?? "";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const isConfigured = () => GOOGLE_ADS_ID.includes("AW-") && !GOOGLE_ADS_ID.includes("XXXX");

/**
 * Leitet gtag-Auufe an window.gtag weiter, wenn die Bibliothek geladen ist.
 * Fällt zurück auf direktes dataLayer.push, wenn noch nicht initialisiert.
 */
export function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** Consent-Regionen: EEA, UK, CH, Quebec. */
const CONSENT_REGIONS = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE", "IS", "LI", "NO", "GB", "CH", "CA-QC",
];

let initialized = false;

/**
 * Initialisiert das Google-Tag (gtag.js).
 *
 * Consent-Mode-Defaults werden VOR dem Laden des externen Skripts gesetzt,
 * damit das gtag.js-Tag die Einwilligungsanforderungen für europäische
 * Besucher korrekt beachtet. Außerhalb der Einwilligungsregionen ist
 * Tracking standardmäßig erlaubt.
 */
export function initTracking() {
  if (typeof window === "undefined" || initialized) return;
  if (!isConfigured()) return;
  initialized = true;

  // dataLayer und window.gtag definieren, falls noch nicht vorhanden.
  // Das Head-Script definiert diese bereits vor dem ersten Client-JS.
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  // Consent Mode v2 Defaults — VOR dem Laden von gtag.js setzen.
  // Global: erlaubt. EEA/UK/CH/Quebec: verweigert bis Einwilligung.
  gtag("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
    region: CONSENT_REGIONS,
  });

  // Gespeicherte Wahl wiederherstellen, bevor das Tag lädt.
  try {
    const choice = localStorage.getItem("cookie_consent");
    if (choice === "granted") {
      gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
      });
    } else if (choice === "denied") {
      gtag("consent", "update", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
  } catch {
    /* localStorage nicht verfügbar */
  }

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);

  gtag("js", new Date());
  gtag("config", GOOGLE_ADS_ID);
  if (GA4_ID) gtag("config", GA4_ID);
}

export function trackPageView(path: string) {
  if (!isConfigured()) return;
  gtag("event", "page_view", { page_path: path });
}

/** Feuert die Google-Ads-Conversion beim Absenden des Kontaktformulars. */
export function trackLeadConversion(value = 110) {
  if (typeof window !== "undefined") {
    console.info("[tracking] lead conversion", { value });
  }
  if (!isConfigured()) return;
  gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
    value,
    currency: "EUR",
  });
  gtag("event", "generate_lead", { value, currency: "EUR" });
}

/**
 * Meldet, dass jemand das Formular begonnen hat (erste Eingabe).
 * Reines Messereignis, keine Google-Ads-Conversion.
 */
export function trackFormStart(source: string) {
  if (!isConfigured()) return;
  gtag("event", "form_start", { form_source: source });
}
